class BundleBuilder extends HTMLElement {
  static MOBILE_HEADER = 110;
  constructor() {
    super();
    this.classList.add("loading-init");
    this.confirmBtns = this.querySelectorAll(".bundle-builder__confirm");
    this.addToCartBtn = this.querySelector(".bundle-builder-add-to-cart");
    this.addRow = this.querySelector(".bundle-builder__add-row");
    this.removeIcons = this.querySelectorAll(".icon-remove");
    this.mainRowsContainer = this.querySelector(".bundle-builder__rows");
    this.moreRowsContainer = this.querySelector(".bundle-builder__more-rows");
    this.addRowsIndexEl = this.addRow?.querySelector(".add-row__title-index");
    this.savingsMessage = this.querySelector(
      ".bundle-builder__savings-message"
    );
    this.ignoreOrder = (this.classList.contains("bundle-builder--mixed_bundle")) ? true : false;
    this.styleIcons = this.querySelectorAll(".style-icon");
    this.multiTiresBtns = this.querySelectorAll(".multi-tires__btn");
    this.ignorePackImageSwap = false;
    this.isBlackFridayVariety =
      Array.isArray(window?.productData?.tags) &&
      window.productData.tags.includes("black-friday-variety");
    this.isBlackFriday =
      Array.isArray(window?.productData?.tags) &&
      window.productData.tags.includes("black-friday-giftset");

    if (this.isBlackFriday && !this.isBlackFridayVariety) {
      this.applyBlackFridayGiftsetMode();
    }

    if (this.isBlackFridayVariety) {
      this.applyBlackFridayVarietyMode();
    }

    this.currencyPrefix = this.dataset.currencyPrefix || "";
    this.confirmBtns.forEach((btn) => {
      btn.addEventListener("click", this.onConfirmBtnClick.bind(this));
    });
    this.addToCartBtn.addEventListener(
      "click",
      this.onAddToCartBtnClick.bind(this)
    );
    this.addRow?.addEventListener("click", this.onAddRowClick.bind(this));
    this.removeIcons.forEach((icon) => {
      icon.addEventListener("click", this.onRemoveIconClick.bind(this));
    });
    this.styleIcons.forEach((icon) => {
      icon.addEventListener("click", this.onStyleIconClick.bind(this));
    });
    this.multiTiresBtns.forEach((btn) => {
      btn.addEventListener("click", this.onMultiTiresBtnClick.bind(this));
    });
    window.addEventListener("load", () => {
      const params = new URLSearchParams(window.location.search);
      const pack = params.get("pack");
      if (!pack) {
        this.classList.remove("loading");
        return;
      }
      const btn = this.querySelector(`.multi-tires__btn[data-items="${pack}"]`);
      if (btn) btn.click();
      this.classList.remove("loading");
    });
    document.addEventListener(
      "DOMContentLoaded",
      this.singlesAndPacksSavings.bind(this)
    );
    document.addEventListener(
      "DOMContentLoaded",
      this.initialPackPrice.bind(this)
    );
    this.addEventListener("bundle-row-clicked", () => {
      this.ignorePackImageSwap = true;
    });
    this.updateAddRowTitle();

    this.calcSavings();
    this.updatePackPrice();
    this.updateMixedBundlePrice();
    this.initializeMixedBundlePricing();
    /* If Black friday gift set product, open first/only row to be visible */
    if (this.isBlackFriday || this.isBlackFridayVariety) {
      const openVisibleRow = () => {
        const visibleRow = this.querySelector(
          "bundle-builder-row:not(.hidden)"
        );
        if (visibleRow) {
          // Make sure the element is ready
          requestAnimationFrame(() => {
            visibleRow.classList.add("active");      
            visibleRow.classList.remove("to-select");
            visibleRow.body.style.maxHeight = getComputedStyle(
              visibleRow.querySelector(".bundle-builder__body-inner")
            ).height;
          });
        } else {
          // If rows not yet in DOM, try again shortly
          setTimeout(openVisibleRow, 2500);
        }
      };
      openVisibleRow();
    }

    window.addEventListener("load", () => {
      setTimeout(() => {
        this.checkGiftBoxInventory();
      }, 300);
    });
  }

  static scrollOffset() {
    return 80;
  }

  static scrollRowHeaderIntoView(headerEl, offsetPx = 0, smooth = true) {
    if (!headerEl) return;

    /* ───────── phones: whole document scrolls ───────── */
    if (matchMedia("(max-width: 749px)").matches) {
      const y =
        headerEl.getBoundingClientRect().top + window.scrollY - offsetPx;
      window.scrollTo({ top: y, behavior: smooth ? "smooth" : "auto" });
      return;
    }

    /* ───────── desktops: find inner scroll pane ─────── */
    const root = document.scrollingElement || document.documentElement;
    
    /* Force layout recalculation first to ensure accurate dimensions after dynamic DOM changes */
    void headerEl.offsetHeight;
    
    let container = headerEl.parentElement;


    container = document.querySelector(".product-info__inner");
    /* Force layout recalculation again after container detection to ensure accurate container dimensions */
    void container.offsetHeight;

    const isiOS =
      /iP(ad|hone|od)/.test(navigator.userAgent) && !window.MSStream;
    const wantsSmooth = smooth && !isiOS;

    /* Wait for layout to settle after screen width changes, then recalculate bounding rects */
    requestAnimationFrame(() => {
      // Force layout recalculation after RAF to ensure accurate dimensions after resize
      void container.offsetHeight;
      void headerEl.offsetHeight;
      
      /* target position inside that pane - recalculated after layout settles */
      const cRect = container.getBoundingClientRect();
      const hRect = headerEl.getBoundingClientRect();
      const target = hRect.top - cRect.top + container.scrollTop - offsetPx -10;

      if (isiOS && container !== root)
        container.style.webkitOverflowScrolling = "touch";

      if (isiOS) {
        headerEl.scrollIntoView({ block: "start", behavior: "smooth" });
        if (offsetPx)
          requestAnimationFrame(() =>
            container.scrollBy({ top: -offsetPx, behavior: "auto" })
          );
      } else {
        container.scrollTo({
          top: target,
          behavior: wantsSmooth ? "smooth" : "auto",
        });
      }
    });
  }

  /* Check giftbox inventory */
  async checkGiftBoxInventory() {
    // Only apply on BF products
    const tags = window?.productData?.tags || [];
    const isBF =
      tags.includes("black-friday-giftset") ||
      tags.includes("black-friday-variety") ||
      tags.includes("black-friday-ownpack");

    if (!isBF) return;

    const handle = (window?.productData?.handle || "").toLowerCase();
    let boxHandle = null;

    if (handle.includes("3-pack")) boxHandle = "3-pack-giftbox";
    if (handle.includes("5-pack")) boxHandle = "5-pack-giftbox";

    if (!boxHandle) return;

    try {
      const res = await fetch(`/products/${boxHandle}.js`);
      if (!res.ok) throw new Error("Giftbox JSON not found");

      const product = await res.json();
      const totalInventory = product.variants.reduce(
        (sum, v) => sum + (v.inventory_quantity || 0),
        0
      );

      if (totalInventory <= 0) {
        const btn = this.querySelector(".bundle-builder-add-to-cart");

        // 🛑 Disable ATC button
        if (btn) {
          btn.classList.add("disabled");
          btn.textContent = "Sold out";
        }

        // 🛑 Disable entire bundle-builder interactions
        this.style.pointerEvents = "none";
        this.style.opacity = "0.55";
      }
    } catch (err) {
      console.error("Giftbox inventory check failed:", err);
    }
  }

  onConfirmBtnClick(e) {
    const btn = e.currentTarget;

    /* ---------- 1. locate the <bundle-builder-row> ---------- */
    let thisRow = btn.closest("bundle-builder-row");
    if (!thisRow) {
      const outer = btn.closest(".bundle-builder-row__outer");
      thisRow = outer ? outer.querySelector("bundle-builder-row") : null;
    }
    if (!thisRow) {
      console.warn(
        "Confirm button is not inside a <bundle-builder-row>. " +
          "Check your template or wrappers."
      );
      return;
    }

    /* ---------- 2. house-keeping ---------- */
    const thisStep = btn.closest(".bundle-builder-step");
    const thisRowIndex = Number(thisRow.dataset.index);
    const nextRow =
      thisRow
        .closest(".bundle-builder__rows")
        ?.querySelector(
          `bundle-builder-row[data-index="${thisRowIndex + 1}"]`
        ) || null;

    if (thisStep) {
      const counter = thisStep.querySelector(".selected-counter");
      if (counter && Number(counter.textContent) < thisRowIndex) {
        counter.textContent = thisRowIndex; // bump header counter
      }
    }


    /* ---------- 3. still rows left in this step ---------- */
    if (nextRow) {
      const isMobile = matchMedia("(max-width:749px)").matches;

      /* 2a. collapse current row &  prepare next row */
      if (!this.ignoreOrder){
        thisRow.classList.remove("to-select");
        nextRow.classList.remove("disabled");
        nextRow.classList.add("to-select");
      }

      /* 3. open next row */
      thisRow.header.click();

      if (isMobile) {
        const body = thisRow.querySelector(".bundle-builder__body");
        const openNext = () => nextRow.header.click();
        body
          ? body.addEventListener("transitionend", openNext, { once: true })
          : openNext(); // no animation → open now
      } else {
        // BundleBuilder.scrollRowHeaderIntoView(nextRow.header, 0, false);
        nextRow.header.click();
      }
      
      BundleBuilder.checkATCButton();
      return; // done for this click
    }

    /* ---------- 4. no next row – maybe another step? ---------- */
    if (thisStep) {
      const nextStep = thisStep.nextElementSibling;
      if (nextStep) {
        // close current row & step
        thisRow.classList.remove("to-select");
        thisRow.header.click();

        thisStep.classList.remove("to-select");
        // thisStep.header.click();

        // activate next step
        nextStep.classList.remove("disabled");
        nextStep.classList.add("to-select");
        nextStep.header.click();
      } else {
        // last row of last step – enable Add-to-Cart
        this.onLastConfirmBtnClick(e);
      }
    } else {
      // safety net (shouldn’t happen)
      this.onLastConfirmBtnClick(e);
    }
    BundleBuilder.checkATCButton();
    
  }

  onLastConfirmBtnClick(e) {
    const thisRow = e.target.closest(".bundle-builder-row");
    const thisStep = e.target.closest(".bundle-builder-step");

    // 1. Close the current row
    if (!this.ignoreOrder){
      thisRow.classList.remove("to-select");
    }
    thisRow.header.click();

    // 2. Close the current step
    if (thisStep) {
      thisStep.classList.remove("to-select");
      thisStep.header.click();
    }

    // 3. Activate add to cart button
    // this.addToCartBtn.innerHTML = "Add to cart";
    // this.addToCartBtn.classList.remove("disabled");
    BundleBuilder.checkATCButton();
  }

  static checkATCButton() {
    let allSelected = true;
    document.querySelectorAll(".bundle-builder__rows .bundle-builder-row").forEach((row) => {
      if (row.classList.contains("to-select")) {
        allSelected = false;
        return;
      }
    });
    if (allSelected) {
      document.querySelector(".bundle-builder-add-to-cart").innerHTML = "Add to cart";
      document.querySelector(".bundle-builder-add-to-cart").classList.remove("disabled");
    }else{
      document.querySelector(".bundle-builder-add-to-cart").innerHTML = "Choose Products";
      document.querySelector(".bundle-builder-add-to-cart").classList.add("disabled");
    }
  }

  onAddToCartBtnClick(e) {
    const btn = e.currentTarget;
    const savingsEl = this.querySelector(
      ".bundle-builder__savings-message .savings"
    );

    let bundleSavings = 0;
    if (savingsEl) {
      bundleSavings = savingsEl.textContent.replace(/[^0-9.]/g, "");
    }

    if (btn.classList.contains("disabled") || btn.classList.contains("loading")) {
      if (btn.classList.contains("disabled")) {
        this.highlightToSelectRow();
        this.highlightToSelectStep();
      }
      return;
    }

    const items = [];
    const getPreorderPropertyValue = (variantId) => {
      if (!variantId) return "";

      const input = this.querySelector(
        `.preorder__line-item-property input[data-property-key="_Pre-order"][data-variant-id="${variantId}"]`
      );

      return input?.value?.trim() || "";
    };

    // 🟣 Black Friday giftset logic
    if (this.isBlackFriday) {
      // Only use visible rows for Black Friday bundles
      const rowsContainer = this.querySelector(".bundle-builder__rows");
      const rows = [...rowsContainer.querySelectorAll("bundle-builder-row")];

      rows.forEach((row) => {
        const variantInput = row.querySelector(".product-form__variant-id");
        if (variantInput) {
          const variantId = variantInput.value;
          const properties = {
            _BundleSavings: `$${bundleSavings}`,
          };
          const preorderValue = getPreorderPropertyValue(variantId);

          if (preorderValue) {
            properties["_Pre-order"] = preorderValue;
          }

          items.push({
            id: variantId,
            quantity: 1,
            properties,
          });
        }
      });

      // Add shared bundle info
      const bundleKey = `BF-${Date.now()}`;

      const mainImage = window?.productData?.featured_image
        ? `https:${window.productData.featured_image}`
        : "https://cdn.shopify.com/s/files/1/0000/0000/files/default-placeholder.jpg"; // fallback just in case
      const bundleTitle = window?.productData?.title || "Black Friday Pack";

      items.forEach((item, i) => {
        item.properties["_GiftSet"] = "true";
        item.properties["_BundleKey"] = bundleKey;
        item.properties["_BundleTitle"] = bundleTitle;
        item.properties["_BundleMainImage"] = mainImage;
        item.properties["_BundleItem"] = `Item ${i + 1}`;
      });
      // Detect which box variant to use based on title
      const productTitle = (window?.productData?.title || "").toLowerCase();
      let boxVariantId = "42150281248862"; // default 3-pack

      if (productTitle.includes("5 pack") || productTitle.includes("5-pack")) {
        boxVariantId = "42150281674846"; // use 5-pack box
      }
      // Add Gift Box (only once)
      items.push({
        id: boxVariantId, // BOX variant ID
        quantity: 1,
        properties: {
          _BundleSavings: `$${bundleSavings}`,
          _GiftSet: "true",
          _BundleKey: bundleKey,
          _BundleTitle: bundleTitle,
          _BundleMainImage: mainImage,
          _BundleItem: "Gift Card",
        },
      });
    }
    // BLACK FRIDAY GIFTSET VARIETY
    else if (this.isBlackFridayVariety) {
      const rowsContainer = this.querySelector(".bundle-builder__rows");
      const allRows = [...rowsContainer.querySelectorAll("bundle-builder-row")];
      const items = [];

      // Detect pack count
      let packCount = 3;
      const handle = window?.productData?.handle?.toLowerCase() || "";
      const title = window?.productData?.title?.toLowerCase() || "";
      const activeBtn = this.querySelector(".multi-tires__btn.active");
      if (handle.includes("5-pack") || activeBtn?.dataset.items === "5")
        packCount = 5;

      // Collect first packCount rows (even if hidden)
      allRows.slice(0, packCount).forEach((row) => {
        const variantInput = row.querySelector(".product-form__variant-id");
        if (!variantInput) return;
        const variantId = variantInput.value;
        const properties = { _BundleSavings: `$${bundleSavings}` };
        const preorderValue = getPreorderPropertyValue(variantId);

        if (preorderValue) {
          properties["_Pre-order"] = preorderValue;
        }

        items.push({
          id: variantId,
          quantity: 1,
          properties,
        });
      });

      // Add shared bundle info
      const bundleKey = `BFV-${Date.now()}`;
      const mainImage = window?.productData?.featured_image
        ? `https:${window.productData.featured_image}`
        : "https://cdn.shopify.com/s/files/1/0000/0000/files/default-placeholder.jpg";
      const bundleTitle = window?.productData?.title || "Variety Pack";

      items.forEach((item, i) => {
        item.properties["_GiftSet"] = "true";
        item.properties["_BundleKey"] = bundleKey;
        item.properties["_BundleTitle"] = bundleTitle;
        item.properties["_BundleMainImage"] = mainImage;
        item.properties["_BundleItem"] = `Item ${i + 1}`;
      });
      // Detect which box variant to use based on title
      const productTitle = (window?.productData?.title || "").toLowerCase();
      let boxVariantId = "42150281248862"; // default 3-pack

      if (productTitle.includes("5 pack") || productTitle.includes("5-pack")) {
        boxVariantId = "42150281674846"; // use 5-pack box
      }
      // Add giftset box (only once)
      items.push({
        id: boxVariantId, // replace with real gift card variant ID
        quantity: 1,
        properties: {
          _GiftSet: "true",
          _BundleKey: bundleKey,
          _BundleTitle: bundleTitle,
          _BundleMainImage: mainImage,
          _BundleItem: "Gift Box",
          _BundleSavings: `$${bundleSavings}`,
        },
      });

      console.log(`🛍️ Variety pack (${packCount}) items:`, items);
      this.bundleBuilderATC(items);
      return;
    }
    // 🟣 BLACK FRIDAY OWN PACK LOGIC
    else if (
      Array.isArray(window?.productData?.tags) &&
      window.productData.tags.includes("black-friday-ownpack")
    ) {
      const rowsContainer = this.querySelector(".bundle-builder__rows");
      const allRows = [...rowsContainer.querySelectorAll("bundle-builder-row")];
      const items = [];

      // Collect all variant IDs normally
      allRows.forEach((row) => {
        const variantInput = row.querySelector(".product-form__variant-id");
        if (!variantInput) return;
        const variantId = variantInput.value;
        if (!variantId) return;
        const properties = { _BundleSavings: `$${bundleSavings}` };
        const preorderValue = getPreorderPropertyValue(variantId);

        if (preorderValue) {
          properties["_Pre-order"] = preorderValue;
        }

        items.push({
          id: variantId,
          quantity: 1,
          properties,
        });
      });

      // Shared bundle data
      const bundleKey = `BFO-${Date.now()}`;
      const mainImage =
        window.productData.featured_image ||
        (window.productData.images?.length
          ? window.productData.images[0]
          : null);

      const bundleTitle = window?.productData?.title || "Own Black Friday Pack";

      // Add group info to each selected item
      items.forEach((item, i) => {
        item.properties["_GiftSet"] = "true";
        item.properties["_BundleKey"] = bundleKey;
        item.properties["_BundleTitle"] = bundleTitle;
        item.properties["_BundleMainImage"] = mainImage;
        item.properties["_BundleItem"] = `Item ${i + 1}`;
      });
      // Detect which box variant to use based on title
      const productTitle = (window?.productData?.title || "").toLowerCase();
      let boxVariantId = "42150281248862"; // default 3-pack

      if (productTitle.includes("5 pack") || productTitle.includes("5-pack")) {
        boxVariantId = "42150281674846"; // use 5-pack box
      }
      // Add the gift box
      if (!window._giftCardAddedOnce) {
        items.push({
          id: boxVariantId,
          quantity: 1,
          properties: {
            _GiftSet: "true",
            _BundleKey: bundleKey,
            _BundleTitle: bundleTitle,
            _BundleMainImage: mainImage,
            _BundleItem: "Gift Card",
            _BundleSavings: `$${bundleSavings}`,
          },
        });
        window._giftCardAddedOnce = true;
      }

      console.log(`🛍️ Black Friday Own Pack items:`, items);
      this.bundleBuilderATC(items);
      return;
    }
    // 🟢 Normal bundle logic (everything else)
    else {
      const forms = this.querySelectorAll("form#product-form");
      const variantItemList = {};

      forms.forEach((form) => {
        const isDisabled = form
          .closest(".bundle-builder-row")
          .classList.contains("disabled");
        if (isDisabled) return;

        const styleForm = form.closest(".style-form");
        if (styleForm && styleForm.classList.contains("hidden")) return;

        const variantId = form.querySelector(".product-form__variant-id").value;

        if (variantItemList[variantId]) {
          variantItemList[variantId]++;
        } else {
          variantItemList[variantId] = 1;
        }
      });

      Object.keys(variantItemList).forEach((variantId) => {
      const props = {};

      const finalSaleInput = this.querySelector(
        `.last-chance__line-item-property input.variant--${variantId}`
      );

      if (finalSaleInput && finalSaleInput.value) {
        props["_LPROP"] = finalSaleInput.value;
      }

      const preorderValue = getPreorderPropertyValue(variantId);
      if (preorderValue) {
        props["_Pre-order"] = preorderValue;
      }

      items.push({
        id: variantId,
        quantity: variantItemList[variantId],
        properties: props,
      });
    });

    }

    // ✅ Send to cart
    console.log("🛍️ Final items:", items);
    this.bundleBuilderATC(items);
  }

  highlightToSelectRow() {
    const toSelectRow = document.querySelector(".bundle-builder-row.to-select");
    toSelectRow.classList.add("highlight-to-select");
    setTimeout(() => toSelectRow.classList.remove("highlight-to-select"), 1000);
  }

  highlightToSelectStep() {
    const toSelectStep = document.querySelector(
      ".bundle-builder-step.to-select"
    );
    if (!toSelectStep) return;

    toSelectStep.classList.add("highlight-to-select");
    setTimeout(
      () => toSelectStep.classList.remove("highlight-to-select"),
      1000
    );
  }

  formatWithPrefix(amount) {
    const money = Shopify.formatMoney(amount).replace(/(\.|,)00\b/, "");
    const prefix = this.getCurrencyPrefix();
    return prefix ? prefix + money : money;
  }

  stripSavingsLabelDecimals(value) {
    return String(value ?? "").replace(/([.,]\d{1,2})(?=$|[^0-9])/g, "");
  }

  getCurrencyPrefix() {
    const activeCurrency = window?.Shopify?.currency?.active;
    if (activeCurrency === "CAD") return "C";
    if (activeCurrency === "AUD") return "A";
    return this.currencyPrefix || "";
  }

  applyCurrencyPrefixToText(text) {
    const prefix = this.getCurrencyPrefix();
    if (!prefix) return text;

    return String(text).replace(/(^|[^A-Za-z])\$/g, (match, before) => {
      return `${before}${prefix}$`;
    });
  }
  // Used for gift set with 3 black T-shirts
  applyBlackFridayGiftsetMode() {
    const rows = this.querySelectorAll("bundle-builder-row");
    if (!rows.length) return;

    // Update title + variant text for Black Friday
    rows.forEach((row, i) => {
      const titleInner = row.querySelector(".bundle-builder-row__title-inner");
      const variantEl = row.querySelector(
        ".bundle-builder-row__selected-variant"
      );
      const titleIndex = row.querySelector(".bundle-builder-row__title-index");
      const caret = row.querySelector(".icon-caret");

      if (titleInner) {
        titleInner.textContent = "Gift-set";
        if (titleIndex) titleIndex.textContent = ""; // clear index
      }

      if (variantEl) {
        variantEl.textContent = "Select a size";
      }
    });

    // Hide Next button
    this.confirmBtns.forEach((btn) => {
      btn.style.display = "none";
    });
    // 1. Show only the first row visually
    rows.forEach((row, i) => {
      if (i === 0) {
        row.classList.remove("hidden");
        row.style.display = "";
      } else {
        row.classList.add("hidden");
        row.style.display = "none";
      }
    });

    // 2. Find all size radios in the first row
    const firstRow = rows[0];

    const sizeRadios = firstRow.querySelectorAll(
      '.product-form__input input[type="radio"][name*="Size"], .product-form__input input[type="radio"][data-option-name*="Size"]'
    );

    if (!sizeRadios.length) return;
    // Uncheck all radios initially
    sizeRadios.forEach((radio) => {
      radio.checked = false;
      radio.removeAttribute("checked"); // also remove attribute if it's hardcoded in HTML
    });
    sizeRadios.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        const selectedValue = e.target.value;

        // 3. Sync selected size to all hidden rows
        rows.forEach((r, idx) => {
          if (idx === 0) return;
          const hiddenRadio = r.querySelector(
            `input[type="radio"][value="${selectedValue}"]`
          );
          if (hiddenRadio) {
            hiddenRadio.checked = true;
            hiddenRadio.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });

        // 4. Automatically click all confirm buttons (simulate user confirmation)
        this.confirmBtns.forEach((btn) => {
          // Only click buttons in hidden rows (first row stays as is)
          const parentRow = btn.closest("bundle-builder-row");
          if (parentRow && parentRow !== firstRow) {
            btn.click();
          }
        });

        // 5. Enable Add to Cart button after everything syncs
        this.addToCartBtn.classList.remove("disabled");
        this.addToCartBtn.innerHTML = "Add to cart";
      });
    });
  }

  buildVariantAvailabilityFromRows() {
    const rows = this.querySelectorAll(".product-form--bundle-builder");
    if (!rows.length || !window.giftSetColors) return {};

    const colors = window.giftSetColors.split(",").map((c) => c.trim());
    const availabilityMap = {};

    rows.forEach((row) => {
      const scriptEl = row.querySelector("variant-radios script");
      if (!scriptEl) return;

      try {
        const variants = JSON.parse(scriptEl.textContent);
        variants.forEach((v) => {
          const color = (v.option1 || "").trim();
          const size = (v.option2 || "").trim();
          const available = v.available;

          // Only track colors from metafield
          if (!colors.includes(color)) return;
          if (!availabilityMap[color]) availabilityMap[color] = {};
          availabilityMap[color][size] = available;
        });
      } catch (err) {
        console.warn(`Failed to parse variants for row`, err);
      }
    });

    console.log("🗺️ Built variant availability map:", availabilityMap);
    window.variantAvailability = availabilityMap;
    return availabilityMap;
  }
  // Used for Black Friday Variety modes
  applyBlackFridayVarietyMode() {
    const rows = Array.from(this.querySelectorAll("bundle-builder-row"));
    if (!rows.length) return;

    rows.forEach((row) => {
      const titleInner = row.querySelector(".bundle-builder-row__title-inner");
      const variantEl = row.querySelector(
        ".bundle-builder-row__selected-variant"
      );
      const titleIndex = row.querySelector(".bundle-builder-row__title-index");
      if (titleInner) titleInner.textContent = "Gift-set";
      if (titleIndex) titleIndex.textContent = "";
      if (variantEl) variantEl.textContent = "Select a size";
    });

    async function buildAvailabilityFromMainProduct() {
      const currentHandle = (window.productData?.handle || "").toLowerCase();
      let handle;

      if (currentHandle.includes("women") || currentHandle.includes("ladies")) {
        handle = "womens-merino-crew-neck-tee";
      } else {
        handle = "merino-wool-crew-neck-t-shirt";
      }
      const map = {};
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error("Failed to load product JSON");
        const data = await res.json();
        data.variants.forEach((v) => {
          const color = (v.option1 || "").trim();
          const size = (v.option2 || "").trim();
          if (!color || !size) return;
          if (!map[color]) map[color] = {};
          map[color][size] = v.available;
        });
        console.log("✅ Variant availability map built:", map);
        window.variantAvailability = map;
        return map;
      } catch (err) {
        console.error("❌ Error building availability map:", err);
        return {};
      }
    }

    requestAnimationFrame(() => {
      buildAvailabilityFromMainProduct().then((map) => {
        console.log("🟢 Live map ready:", map);

        let packCount = 3;
        const title = window?.productData?.title?.toLowerCase() || "";
        const handle = window?.productData?.handle?.toLowerCase() || "";
        const activeBtn = this.querySelector(".multi-tires__btn.active");
        if (
          handle.includes("mens-5-pack-gift-set-variety") ||
          handle.includes("women-s-5-pack-gift-set-variety") ||
          activeBtn?.dataset.items === "5"
        )
          packCount = 5;
        this.confirmBtns.forEach((btn) => (btn.style.display = "none"));
        rows.forEach((row) => {
          row.classList.add("hidden", "disabled");
          row.style.display = "none";
        });
        const firstRow = rows[0];
        if (firstRow) {
          firstRow.classList.remove("hidden", "disabled");
          firstRow.style.display = "";
        }

        let colorOrder = [];
        if (window.giftSetColors)
          colorOrder = window.giftSetColors.split(",").map((c) => c.trim());

        rows.forEach((row, i) => {
          const desiredColor = colorOrder[i];
          if (!desiredColor) return;
          const colorInputs = row.querySelectorAll('input[option="Color"]');
          if (!colorInputs.length) return;
          colorInputs.forEach((inp) => (inp.checked = false));
          const match = Array.from(colorInputs).find(
            (inp) =>
              inp.value.trim().toLowerCase() ===
              desiredColor.trim().toLowerCase()
          );
          if (!match) return;
          match.checked = true;
          match.dispatchEvent(new Event("change", { bubbles: true }));
          const label = row.querySelector(".option-heading__current-color");
          if (label) label.textContent = desiredColor;
          const variantIdInput = row.querySelector(".product-form__variant-id");
          const vr = row.querySelector("variant-radios");
          if (variantIdInput && vr?.dataset?.variants) {
            try {
              const variants = JSON.parse(vr.dataset.variants || "[]");
              const v = variants.find(
                (v) =>
                  (v.option1 || "").trim().toLowerCase() ===
                  desiredColor.trim().toLowerCase()
              );
              if (v) variantIdInput.value = v.id;
            } catch (_) {}
          }
        });

        const combinedColors = colorOrder.filter(Boolean).join(" / ");
        const firstVariantLabel = this.querySelector(
          ".bundle-builder-row__selected-variant"
        );
        if (firstVariantLabel && combinedColors)
          firstVariantLabel.textContent = combinedColors;

        const sizeRadios = firstRow.querySelectorAll(
          '.product-form__input input[type="radio"][option="Size"]'
        );
        if (!sizeRadios.length) return;

        const atcBtn = this.querySelector(".bundle-builder-add-to-cart");
        if (!atcBtn) return;

        // ✅ Fixed size syncing across all rows
        sizeRadios.forEach((radio) => {
          radio.addEventListener("change", (e) => {
            const selectedSize = e.target.value;

            // 🔄 Sync this same size across all rows
            rows.forEach((row, idx) => {
              if (idx === 0) return;
              const sameSize = row.querySelector(
                `input[type="radio"][option="Size"][value="${selectedSize}"]`
              );
              if (sameSize) {
                sameSize.checked = true;
                sameSize.dispatchEvent(new Event("change", { bubbles: true }));
              }
            });

            // 🧠 Availability check
            let allAvailable = true;
            for (const color of colorOrder) {
              const isAvailable = map?.[color]?.[selectedSize];
              if (!isAvailable) {
                allAvailable = false;
                break;
              }
            }

            if (!allAvailable) {
              atcBtn.classList.add("disabled");
              atcBtn.textContent = "Sold out";
            } else {
              atcBtn.classList.remove("disabled");
              atcBtn.textContent = "Add to cart";
            }
          });
        });

        const checkedSize = firstRow.querySelector(
          'input[option="Size"]:checked'
        );
        if (checkedSize) {
          const selectedSize = checkedSize.value;
          let allAvailable = true;
          for (const color of colorOrder) {
            const isAvailable = map?.[color]?.[selectedSize];
            if (!isAvailable) {
              allAvailable = false;
              break;
            }
          }
          if (!allAvailable) {
            atcBtn.classList.add("disabled");
            atcBtn.textContent = "Sold out";
          } else {
            atcBtn.classList.remove("disabled");
            atcBtn.textContent = "Add to cart";
          }
        }

        atcBtn.addEventListener("click", (e) => {
          if (atcBtn.classList.contains("disabled")) {
            e.preventDefault();
            return;
          }
          atcBtn.classList.add("loading");
          setTimeout(() => atcBtn.classList.remove("loading"), 700);
        });

        document.addEventListener("cart:updated", () => {
          atcBtn.classList.remove("disabled", "loading");
          atcBtn.textContent = "Add to cart";
        });
      });
    });
  }

  updateAddRowTitle() {
    if (!this.addRow || !this.savingsMessage) return;

    const titleEl = this.addRow.querySelector(".add-row__title");
    if (!titleEl) return;

    const indexSpan = titleEl.querySelector(".add-row__title-index");
    if (!indexSpan) return;

    const full = Number(this.savingsMessage.dataset.productPriceOriginal);
    const currentRows = this.mainRowsContainer.childElementCount;
    const nextRows = currentRows + 1;

    let currentTierPricePerItem = Number(this.savingsMessage.dataset.productPriceDiscount);
    let nextTierPricePerItem = null;

    const sortedBtns = [...this.multiTiresBtns].sort(
      (a, b) => parseInt(a.dataset.items) - parseInt(b.dataset.items)
    );
    for (const btn of sortedBtns) {
      const btnItems = parseInt(btn.dataset.items);
      if (btnItems <= currentRows) {
        currentTierPricePerItem = Number(btn.dataset.pricePerItem);
      }
      if (btnItems === nextRows) {
        nextTierPricePerItem = Number(btn.dataset.pricePerItem);
      }
    }

    let noteSpan = titleEl.querySelector(".extra-savings-note");

    if (nextTierPricePerItem === null) {
      if (noteSpan) noteSpan.remove();
      return;
    }

    const currentSavings = (full - currentTierPricePerItem) * currentRows;
    const nextSavings = (full - nextTierPricePerItem) * nextRows;
    const extra = nextSavings - currentSavings;
    const formatted = Shopify.formatMoney(extra).replace(/(\.|,)00\b/, "");

    if (extra <= 0) {
      if (noteSpan) noteSpan.remove();
      return;
    }

    if (!noteSpan) {
      noteSpan = document.createElement("span");
      noteSpan.className = "extra-savings-note";
      indexSpan.after(noteSpan);
    }

    noteSpan.textContent = ` – save an extra ${formatted}!`;
  }

  async bundleBuilderATC(items) {
    const cart = document.querySelector("cart-drawer");
    const cartCounterIcon = document.querySelector(
      ".header__icon--cart-wrapper"
    );

    // Add loading animation
    this.addToCartBtn.classList.add("loading");

    try {
      if (!cart) throw new Error("Cart drawer element not found");

      const formData = {
        sections: cart.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
        items,
      };

      cart.setActiveElement(document.activeElement);

      // 🛒 Add bundle items
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const response = await res.json();

      // 🕒 Wait 2.5 seconds to ensure all items finish processing (tweak as needed)
      console.log("⏳ Waiting 2500ms before rendering cart...");
      await new Promise((r) => setTimeout(r, 2500));

      // 🧩 Ensure response has sections before render
      if (response?.sections) {
        cart.renderContents(response);
        console.log("✅ Cart contents rendered successfully.");
      } else {
        console.warn("⚠️ No sections found in response — retrying fetch...");
        // 🌀 Re-fetch the cart state and then render
        const state = await (
          await fetch("/?sections=cart-drawer,cart-icon-bubble")
        ).json();
        cart.renderContents({ sections: state });
      }

      // ✅ Open cart drawer
      cart.openCloseCart();
    } catch (error) {
      console.error("❌ bundleBuilderATC failed:", error);
    } finally {
      // Remove loading states
      this.addToCartBtn.classList.remove("loading");
      if (cart?.classList.contains("is-empty")) {
        cart.classList.remove("is-empty");
      }
    }
  }

  onAddRowClick() {
    const addRowsIndexNumber = parseInt(this.addRowsIndexEl.textContent);
    const toAddRowOuter = this.moreRowsContainer.firstElementChild;
    if (!toAddRowOuter) return;
    const toAddRow = toAddRowOuter.querySelector(".bundle-builder-row");
    const toSelectRow = document.querySelector(".bundle-builder-row.to-select");

    // 1. If there's no to-select row, make this new row to-select and disable ATC button
    if (!this.ignoreOrder){
      if (!toSelectRow) {
        toAddRow.classList.remove("disabled");
        toAddRow.classList.add("to-select");
        this.addToCartBtn.classList.add("disabled");
      }
    }

    

    // 2. Move the new row to the main rows container
    this.mainRowsContainer.insertAdjacentElement("beforeend", toAddRowOuter);

    // 3. Show/hide remove icon
    this.showHideRemoveIcons();

    // 4. Display the new row
    toAddRowOuter.classList.remove("hidden");

    // 5. Increment the add row index
    this.addRowsIndexEl.textContent = addRowsIndexNumber + 1;

    // 6. Hide add row element if there are no more rows
    if (!this.moreRowsContainer.firstElementChild)
      this.addRow.classList.add("hidden");

    // 7. Re-calc the savings
    this.calcSavings();

    // 8. Update multi-tires buttons
    this.updateMultiTiresBtns();

    // 9. Update pack price
    this.updatePackPrice();

    // 10. Show discount amount
    this.updateAddRowTitle();

    // 11. Check ATC button
    BundleBuilder.checkATCButton();
  }

  showHideRemoveIcons() {
    const addedRows = this.mainRowsContainer.querySelectorAll(
      ".bundle-builder-row__outer"
    );
    if (addedRows.length === 0) return;
    addedRows.forEach((row) => row.classList.remove("show-remove-icon"));
    addedRows[addedRows.length - 1].classList.add("show-remove-icon");
  }

  onRemoveIconClick(e) {
    const addRowsIndexNumber = parseInt(this.addRowsIndexEl.textContent);
    const toRemoveRowOuter = e.target.closest(".bundle-builder-row__outer");
    const toRemoveRow = toRemoveRowOuter.querySelector(".bundle-builder-row");
    const selectedVariantEl = toRemoveRow.querySelector(
      ".bundle-builder-row__selected-variant"
    );

    // 1. Move the row from the main rows container to the more rows container
    this.moreRowsContainer.insertAdjacentElement(
      "afterbegin",
      toRemoveRowOuter
    );

    // 2. Hide and disable it
    toRemoveRowOuter.classList.add("hidden");
    toRemoveRow.classList.remove("to-select");
    toRemoveRow.classList.add("disabled");
    selectedVariantEl.textContent = "";

    // 3. Update add row index and show it
    this.addRowsIndexEl.textContent = addRowsIndexNumber - 1;
    this.addRow.classList.remove("hidden");

    // 4. Show/hide remove icon
    this.showHideRemoveIcons();

    // 5. Check to-select row
    const toSelectRow = document.querySelector(".bundle-builder-row.to-select");
    if (!toSelectRow) {
      this.addToCartBtn.innerHTML = "Add to cart";
      this.addToCartBtn.classList.remove("disabled");
    }

    // 6. If no remaining active rows are restricted (out-of-stock), clear
    //    the restricted state that was left behind by the removed row.
    const remainingRows = this.mainRowsContainer.querySelectorAll("bundle-builder-row");
    const anyRestricted = Array.from(remainingRows).some((row) =>
      row.classList.contains("restricted")
    );
    if (!anyRestricted) {
      this.addToCartBtn.classList.remove("restricted");
      this.querySelectorAll(".bundle-builder-row__header").forEach((el) =>
        el.classList.remove("restricted")
      );
      this.querySelectorAll(".bundle-builder-step__header").forEach((el) =>
        el.classList.remove("restricted")
      );
    }


    // 7. Re-calc the savings
    this.calcSavings();

    // 8. Update multi-tires buttons
    this.updateMultiTiresBtns();

    // 9. Update pack price
    this.updatePackPrice();

    // 10. Show discount amount
    this.updateAddRowTitle();
  }

  // Remove accidental <br> + whitespace inside money spans and set clean text
  setMoneyText(el, text) {
  if (!el) return;

  // Remove any <br> tags that may exist
  el.querySelectorAll("br").forEach((br) => br.remove());

  let clean = String(text)
    .replace(/\s+/g, " ")            // collapse whitespace/newlines
    .trim()
    .replace(/^([^\d-]+)\s+/, "$1"); // remove space after currency prefix

  el.textContent = clean;
}

  getActiveBundleForms() {
    return [...this.querySelectorAll("form#product-form")].filter((form) => {
      const row = form.closest(".bundle-builder-row");
      if (!row || row.classList.contains("disabled")) return false;

      const styleForm = form.closest(".style-form");
      if (styleForm && styleForm.classList.contains("hidden")) return false;

      return true;
    });
  }

  getCurrentVariantForForm(form) {
    const variantRadiosEl = form?.querySelector("variant-radios");
    if (!variantRadiosEl) return null;

    if (typeof variantRadiosEl.getVariantJSON !== "function") {
      return this.getFallbackVariantForForm(form, variantRadiosEl);
    }

    if (!variantRadiosEl.currentVariant) {
      variantRadiosEl.getVariantJSON();
      variantRadiosEl.getSelectedOptions();
      variantRadiosEl.getSelectedVariant();
    }

    return variantRadiosEl.currentVariant || null;
  }

  getFallbackVariantForForm(form, variantRadiosEl) {
    const variantJsonScript = variantRadiosEl.querySelector(
      '[type="application/json"]'
    );
    if (!variantJsonScript) return null;

    let variants = [];
    try {
      variants = JSON.parse(variantJsonScript.textContent);
    } catch (error) {
      return null;
    }

    const selectedOptions = [...variantRadiosEl.querySelectorAll(".js-product-form-option")]
      .map((fieldset) =>
        fieldset.querySelector("input:checked")?.value
      )
      .filter(Boolean);

    if (!selectedOptions.length) return null;

    return (
      variants.find((variant) =>
        Array.isArray(variant.options) &&
        variant.options.every((option, index) => option === selectedOptions[index])
      ) || null
    );
  }

  initializeMixedBundlePricing() {
    if (!this.classList.contains("bundle-builder--mixed_bundle")) return;

    const applyPricing = () => {
      this.updateMixedBundlePrice();
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", applyPricing, {
        once: true,
      });
    } else {
      requestAnimationFrame(applyPricing);
    }

    if (typeof customElements?.whenDefined === "function") {
      customElements.whenDefined("variant-radios").then(() => {
        requestAnimationFrame(applyPricing);
      });
    }

    window.addEventListener(
      "load",
      () => {
        requestAnimationFrame(applyPricing);
      },
      { once: true }
    );
  }

  calculateMixedBundleTotals() {
    if (!this.classList.contains("bundle-builder--mixed_bundle")) {
      return null;
    }

    let subtotalCents = 0;
    let compareCents = 0;
    const fixedBundleDiscountCents = Number(
      this.dataset.fixedBundleDiscount || 0
    );
    this.getActiveBundleForms().forEach((form) => {
      const variant = this.getCurrentVariantForForm(form);
      if (!variant) return;

      const row = form.closest(".bundle-builder-row");
      const rowLastChanceSavings = Number.parseFloat(
        row?.dataset?.lastChanceSavings || "0"
      );
      const variantPrice = Number(variant.price || 0);
      const variantComparePrice = Number(variant.compare_at_price || 0);
      const referencePrice =
        variantComparePrice > variantPrice
          ? variantComparePrice
          : variantPrice + Math.round(rowLastChanceSavings * 100);

      subtotalCents += variantPrice;
      compareCents += referencePrice;
    });

    const totalCents = Math.max(compareCents - fixedBundleDiscountCents, 0);
    const savingsCents = Math.max(compareCents - totalCents, 0);

    return {
      totalCents,
      savingsCents,
      formattedPrice: this.formatWithPrefix(totalCents),
      formattedSavings: this.stripSavingsLabelDecimals(
        this.formatWithPrefix(savingsCents)
      ),
    };
  }

  updateMixedBundlePrice() {
    const totals = this.calculateMixedBundleTotals();
    if (!totals || totals.totalCents <= 0) return;

    const desktopPrice = document.querySelector(
      ".product-info-above-description-desktop .product-price"
    );
    const mobilePrice = document.querySelector(
      ".product-info-above-description-mobile .product-price"
    );
    const mixedBundlePrices = document.querySelectorAll(".mixed-bundle-price");
    const savingsLabels = document.querySelectorAll(
      ".product-info-above-description-desktop .product-price__saving-label, .product-info-above-description-mobile .product-price__saving-label"
    );
    const afterpayPrice = document.querySelector(".afterpay-price");

    if (desktopPrice) desktopPrice.classList.add("hidden");
    if (mobilePrice) mobilePrice.classList.add("hidden");

    mixedBundlePrices.forEach((el) => {
      el.textContent = totals.formattedPrice;
      el.classList.remove("hidden");
    });

    savingsLabels.forEach((label) => {
      if (totals.savingsCents > 0) {
        label.textContent = `Save ${totals.formattedSavings}`;
        label.classList.remove("hidden");
        label.style.display = "";
      } else {
        label.textContent = "";
        label.classList.add("hidden");
        label.style.display = "none";
      }
    });

    if (afterpayPrice) {
      afterpayPrice.innerHTML = this.formatWithPrefix(
        Math.round(totals.totalCents / 4)
      );
    }

    if (this.savingsMessage) {
      this.savingsMessage.dataset.basePrice = totals.formattedPrice;
      this.savingsMessage.dataset.bundleBaseSaveAmount = totals.formattedSavings;
    }
  }



  calcSavings() {
    if (this.classList.contains("bundle-builder--mixed_bundle")) return;

    const numberOfItems = this.mainRowsContainer.childElementCount;

    const originalUnitCents = Number(
      this.savingsMessage?.dataset?.productPriceOriginal
    );
    let discountUnitCents = Number(
      this.savingsMessage?.dataset?.productPriceDiscount
    );

    this.multiTiresBtns.forEach((btn) => {
      const btnItems = parseInt(btn.dataset.items);
      if (numberOfItems >= btnItems) {
        discountUnitCents = Number(btn.dataset.pricePerItem);
      }
    });

    let totalCents = discountUnitCents * numberOfItems;
    let savingsCents = originalUnitCents * numberOfItems - totalCents;

    // Pack products with a saving label use the main product's fixed bundle
    // discount as the source of truth. This keeps the builder aligned with the
    // price applied in cart when the configurable per-item tier is stale.
    const fixedBundleDiscountCents = Number(this.dataset.fixedBundleDiscount || 0);
    const packThreshold = Number.parseInt(
      this.savingsMessage?.dataset?.packThreshold || "0",
      10
    );
    if (
      fixedBundleDiscountCents > 0 &&
      numberOfItems === packThreshold
    ) {
      totalCents = originalUnitCents * numberOfItems - fixedBundleDiscountCents;
      savingsCents = fixedBundleDiscountCents;
    }

    const totalEl = this.savingsMessage?.querySelector(".total");
    const savingsEl = this.savingsMessage?.querySelector(".savings");

    if (this.savingsMessage && this.savingsMessage?.dataset?.basePrice) this.savingsMessage.dataset.basePrice = this.formatWithPrefix(totalCents);
    if (savingsEl)
      this.savingsMessage.dataset.bundleBaseSaveAmount =
        this.formatWithPrefix(savingsCents);

    document.querySelectorAll("bundle-builder-row").forEach((row) => {
      if (
        row.dataset.lastChanceSavings &&
        row.dataset.lastChanceSavings != "0"
      ) {
        savingsCents += parseFloat(row.dataset.lastChanceSavings) * 100;
        totalCents -= parseFloat(row.dataset.lastChanceSavings) * 100;
      }
    });

   if (totalEl) this.setMoneyText(totalEl, this.formatWithPrefix(totalCents));
   if (savingsEl) this.setMoneyText(savingsEl, this.formatWithPrefix(savingsCents));

  }

  onStyleIconClick(e) {
    const styleIcon = e.target.closest(".style-icon");
    const thisRow = styleIcon.closest(".bundle-builder-row");
    const allForms = thisRow.querySelectorAll(".style-form");
    const relatedForm = thisRow.querySelector(styleIcon.dataset.form);
    const variantRadiosEl = relatedForm.querySelector("variant-radios");

    // stopImmediatePropagation
    const input = styleIcon.querySelector('input[type="radio"]');
    input.addEventListener("click", function (e) {
      e.stopImmediatePropagation();
    });

    // 1. Show related form
    allForms.forEach((form) => form.classList.add("hidden"));
    relatedForm.classList.remove("hidden");

    // 2. Upate info
    variantRadiosEl.getSelectedOptions();
    variantRadiosEl.getVariantJSON();
    variantRadiosEl.getSelectedVariant();
    variantRadiosEl.updateProductInfo();
    variantRadiosEl.updateBundleBuilder();
  }


  swapPictureSources(imageUrl) {
    // 1) find the very first slide in the slider
    const slide = document.querySelector(
      ".product-media__gallery--slider .keen-slider__slide"
    );
    if (!slide) return;

    // 2) inside that slide, find the <picture> block
    const picture = slide.querySelector("picture");
    if (!picture) return;

    // 3) swap every <source> srcset to the new URL
    picture.querySelectorAll("source").forEach((src) => {
      // keep the " 1x" or " 2x" suffix if you want; here we just use 1x
      src.srcset = imageUrl + " 1x";
    });

    // 4) swap the <img> itself
    const img = picture.querySelector("img.image-cover");
    if (!img) return;
    img.src = imageUrl;
    img.srcset = "";
    img.setAttribute(
      "data-zoom",
      imageUrl.replace(/(\.\w+)(\?.*)?$/, "_2000x$1$2")
    );
  }

 

onMultiTiresBtnClick(e) {
    const btn = e.currentTarget;
    const requiredNumberOfRows = parseInt(btn.dataset.items);
    const currentNumberOfRows = this.mainRowsContainer.childElementCount;

    document.title = document.title.replace(/\d+/, requiredNumberOfRows);

    this.multiTiresBtns.forEach((el) => el.classList.remove("active"));
    btn.classList.add("active");

    // ✅ Resetuj lastSavings za sve neaktivne buttone jer mijenjamo pack size
    this.multiTiresBtns.forEach((b) => {
      if (!b.classList.contains("active")) {
        delete b.dataset.lastSavings;
      }
    });

    if (requiredNumberOfRows > currentNumberOfRows) {
      const difference = requiredNumberOfRows - currentNumberOfRows;
      for (let index = 0; index < difference; index++) {
        this.addRow.click();
      }
    } else if (requiredNumberOfRows < currentNumberOfRows) {
      const difference = currentNumberOfRows - requiredNumberOfRows;
      for (let index = 0; index < difference; index++) {
        const removeIconsInTheMainRowsContainer =
          this.mainRowsContainer.querySelectorAll(".icon-remove");
        const lastRemoveIcon =
          removeIconsInTheMainRowsContainer[
            removeIconsInTheMainRowsContainer.length - 1
          ];
        lastRemoveIcon.click();
      }
    }

      BundleBuilder.checkATCButton();
    
    this.swapPictureSources(btn.dataset.image);
    this.updateAddRowTitle();
    this.calcSavings();
    this.updateMultiTiresBtns();

    // Update badge to reflect savings for the selected tier
    const originalUnitCents = Number(this.savingsMessage?.dataset?.productPriceOriginal);
    const discountUnitCents = Number(btn.dataset.pricePerItem);
    const savingsCents = (originalUnitCents - discountUnitCents) * requiredNumberOfRows;
    const formattedSavings = this.stripSavingsLabelDecimals(this.formatWithPrefix(savingsCents));
    if (this.savingsMessage) {
      this.savingsMessage.dataset.singlesAndPacksSavings = `Save ${formattedSavings}`;
    }
    this.updatePackPrice();
  }


updateMultiTiresBtns() {
  if (this.multiTiresBtns.length === 0) return;
  var currentNumberOfRows = this.mainRowsContainer.childElementCount;

  if (!this._savedSavingsByPack) this._savedSavingsByPack = {};

  var matchedBtn = null;
  this.multiTiresBtns.forEach((btn) => {
    var btnItems = parseInt(btn.dataset.items);
    if (currentNumberOfRows >= btnItems) {
      this.multiTiresBtns.forEach((el) => el.classList.remove("active"));
      btn.classList.add("active");
      matchedBtn = btn;
    }
  });

  var activeBtn = matchedBtn || this.querySelector(".multi-tires__btn.active");
  if (!activeBtn) return;

  var savingsEl = this.savingsMessage?.querySelector(".savings");
  var currentSavings = savingsEl
    ? this.stripSavingsLabelDecimals(this.applyCurrencyPrefixToText(savingsEl.textContent.trim()))
    : null;

  if (currentSavings) {
    this._savedSavingsByPack[currentNumberOfRows] = currentSavings;
    activeBtn.textContent = `Save ${currentSavings} / ${currentNumberOfRows} pack`;
  }

  var originalUnitCents = Number(this.savingsMessage?.dataset?.productPriceOriginal);
  this.multiTiresBtns.forEach((btn) => {
    if (btn.classList.contains("active")) return;
    var btnItems = parseInt(btn.dataset.items);
    if (this._savedSavingsByPack[btnItems]) {
      btn.textContent = `Save ${this._savedSavingsByPack[btnItems]} / ${btnItems} pack`;
    } else {
      var discountUnitCents = Number(btn.dataset.pricePerItem);
      var savings = (originalUnitCents - discountUnitCents) * btnItems;
      btn.textContent = `Save ${this.stripSavingsLabelDecimals(this.formatWithPrefix(savings))} / ${btnItems} pack`;
    }
  });
}
  singlesAndPacksSavings() {
    const holderEl = document.querySelector(
      ".singles-and-packs__savings-holder"
    );
    if (!holderEl) return;
    const savingsEls = document.querySelectorAll(".singles-and-packs__savings");

    const rawSavings = this.savingsMessage.dataset.singlesAndPacksSavings || "";
    const formattedSavings = this.stripSavingsLabelDecimals(this.applyCurrencyPrefixToText(rawSavings));
    holderEl.textContent = formattedSavings;
    savingsEls.forEach((el) => (el.textContent = holderEl.textContent));
  }

  toCents(priceText) {
    const txt = priceText.trim();
    if (/[.,]\d{2}\b/.test(txt)) {
      return Number(txt.replace(/[^\d]/g, ""));
    }
    return Number(txt.replace(/[^\d]/g, "")) * 100;
  }

  initialPackPrice() {
    if (this.classList.contains("bundle-builder--pack")) {
      const packPrice = document.querySelectorAll(".pack-price");
      if (!packPrice.length > 0) return;
      const initialPackPrice = this.savingsMessage.querySelector(".total");

      const first = this.formatWithPrefix(
        this.toCents(this.savingsMessage.querySelector(".total").textContent)
      );
      packPrice.forEach((price) => (price.textContent = first));
      // if current product is bundle -> show pack price
      if (document.querySelector(".singles-and-packs") === null) {
        const singlePrice = document.querySelectorAll(".product-price");
        packPrice.forEach((price) => price.classList.remove("hidden"));
        singlePrice.forEach((price) => price.classList.add("hidden"));
      }
    } else {
      this.updatePackPrice();
    }
  }

  updatePackPrice() {
    if (this.classList.contains("bundle-builder--mixed_bundle")) return;

    const singlePrice = document.querySelectorAll(".product-price");
    const packPrice = document.querySelectorAll(".pack-price");
    const currentPackPrice = this.savingsMessage.querySelector(".total");
    const savingsLabel = document.querySelectorAll(
      ".product-price__saving-label"
    );
    const currentSavings = this.savingsMessage.querySelector(".savings");
    const singlesAndPacksSavings =
      this.savingsMessage?.dataset?.singlesAndPacksSavings?.trim();
    const rawPackThreshold = this.savingsMessage?.dataset?.packThreshold?.trim();
    const packThreshold = Number.parseInt(rawPackThreshold, 10);

    // Helper function to remove trailing .00 or ,00 if present
    function formatPrice(priceText) {
      priceText = priceText.trim();
      return this.stripSavingsLabelDecimals(this.applyCurrencyPrefixToText(priceText));
    }

    // 1. Pack price - update text content with formatted price
    if (currentPackPrice && currentPackPrice.textContent.trim()) {
      const cents = this.toCents(currentPackPrice.textContent);
      const formattedPrice = this.formatWithPrefix(cents);
      packPrice.forEach((el) => (el.textContent = formattedPrice));
      // 2. Update afterpay price — only when the pack price is the one on display.
      // On a singles PDP with a pack upsell the headline price is the single
      // variant, so writing the pack instalment here would contradict it.
      const afterpayPrice = document.querySelector(".afterpay-price");
      if (afterpayPrice && document.querySelector(".singles-and-packs") === null) {
        afterpayPrice.innerHTML = this.formatWithPrefix(Math.round(cents / 4));
      }
    }

    if (document.querySelector(".singles-and-packs") === null) {
      singlePrice.forEach((price) => price.classList.add("hidden"));
      packPrice.forEach((price) => price.classList.remove("hidden"));
    }

    // 3. Savings label
    if (savingsLabel.length > 0) {
      savingsLabel.forEach((label) => {
        const rawSavings =
          currentSavings?.textContent?.trim() ||
          this.savingsMessage?.dataset?.bundleBaseSaveAmount?.trim();

        if (singlesAndPacksSavings) {
          label.textContent = this.stripSavingsLabelDecimals(this.applyCurrencyPrefixToText(singlesAndPacksSavings));
          label.classList.remove("hidden");
          label.style.display = "";
        } else if (rawSavings) {
          const formattedSavings = formatPrice.call(this, rawSavings);
          const packCopy = Number.isFinite(packThreshold) && packThreshold > 0
            ? ` on ${packThreshold} ${packThreshold === 1 ? "pack" : "packs"}`
            : "";

          label.textContent = `Save ${formattedSavings}${packCopy}`;
          label.classList.remove("hidden");
          label.style.display = "";
        } else {
          label.classList.add("hidden");
        }
      });
    }
  }
}

customElements.define("bundle-builder", BundleBuilder);

class BundleBuilderRow extends HTMLElement {
  constructor() {
    super();
    this.header = this.querySelector(".bundle-builder-row__header");
    this.body = this.querySelector(".bundle-builder__body");
    this.ignoreOrder = this.closest("bundle-builder").ignoreOrder;
    this.header.addEventListener("click", this.onHeaderClick.bind(this));
    window.addEventListener("resize", this.heightAnimation.bind(this));
  }
  onHeaderClick() {
    // 1) If this row is disabled, flash and bail
    if (this.classList.contains("disabled")) {
      this.highlightToSelectRow();
      return;
    }

    // 2) Collapse every other row
    const collapsingRows = [];
    if (!this.isBlackFriday && !this.isBlackFridayVariety) {
      document.querySelectorAll("bundle-builder-row").forEach((row) => {
        if (row !== this && row.classList.contains("active")) {
          collapsingRows.push(row);
          row.classList.remove("active");
          row.body.style.maxHeight = "0";
        }
      });
    }


    // 3) Toggle this one open
    const opening = !this.classList.contains("active");
    if (opening) {
      this.classList.add("active");

      if (this.ignoreOrder){
      this.classList.remove("to-select");
      }
      this.updateRowInfo();
      BundleBuilder.checkATCButton();
      this.heightAnimation();

      // After the open animation completes, remove the fixed max-height so
      // dynamic content (style-forms, lazy images, etc.) can expand freely.
      this.body.addEventListener(
        "transitionend",
        () => {
          if (this.classList.contains("active")) {
            this.body.style.maxHeight = "none";
          }
        },
        { once: true }
      );

      const offset = matchMedia("(max-width:749px)").matches
        ? BundleBuilder.MOBILE_HEADER
        : 0;

      const parentBuilder = this.closest("bundle-builder");
      const isBF = parentBuilder?.isBlackFriday || parentBuilder?.isBlackFridayVariety;

      if (!isBF) {
        // Wait for collapse animations to complete before scrolling
        if (collapsingRows.length > 0) {
          let completedCount = 0;
          const scrollAfterAnimation = () => {
            completedCount++;
            if (completedCount === collapsingRows.length) {
              setTimeout(() => {
                BundleBuilder.scrollRowHeaderIntoView(this.header, offset);
              }, 300);
            }
          };

          collapsingRows.forEach((row) => {
            if (row.body) {
              row.body.addEventListener("transitionend", scrollAfterAnimation, { once: true });
            }
          });
        } else {
          // No rows collapsing, scroll immediately
          setTimeout(() => {
            BundleBuilder.scrollRowHeaderIntoView(this.header, offset);
          }, 300);
        }
      }
    } else {
      // 4) closing — if max-height was released to "none", pin it to the
      // current scroll height first so the CSS transition has a start value.
      if (this.body.style.maxHeight === "none") {
        this.body.style.maxHeight = this.body.scrollHeight + "px";
      }
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.classList.remove("active");
          this.body.style.maxHeight = "0";
        }, 450);
      });
    }


    // 5) bubble up so bundle-builder knows we clicked
    this.dispatchEvent(
      new CustomEvent("bundle-row-clicked", { bubbles: true })
    );
  }

  heightAnimation() {
    if (this.classList.contains("active")) {
      // If max-height was already released to "none" (post-open-animation),
      // don't re-constrain it — dynamic content (sold-out, blend msg, etc.)
      // will already display in full.
      if (this.body.style.maxHeight === "none") return;
      const innerH = getComputedStyle(
        this.querySelector(".bundle-builder__body-inner")
      ).height;
      this.body.style.maxHeight = innerH;
    } else {
      this.body.style.maxHeight = "0";
    }
  }

  highlightToSelectRow() {
    if (this.isBlackFriday && !this.isBlackFridayVariety) {
      const toSelectRow = document.querySelector(
        ".bundle-builder-row.to-select"
      );
      toSelectRow.classList.add("highlight-to-select");
      setTimeout(
        () => toSelectRow.classList.remove("highlight-to-select"),
        1000
      );
    }
  }

  updateRowInfo() {
    const styleForms = this.querySelector(".product-style__style-forms");
    let variantRadiosEl;

    if (styleForms) {
      variantRadiosEl = this.querySelector(
        ".style-form:not(.hidden)"
      ).querySelector("variant-radios");
    } else {
      variantRadiosEl = this.querySelector("variant-radios");
    }

    variantRadiosEl.getSelectedOptions();
    variantRadiosEl.getVariantJSON();
    variantRadiosEl.getSelectedVariant();
    variantRadiosEl.updateProductInfo();
    variantRadiosEl.updateBundleBuilder();
  }
}

customElements.define("bundle-builder-row", BundleBuilderRow);

class BundelBuilderStep extends HTMLElement {
  constructor() {
    super();
    this.header = this.querySelector(".bundle-builder-step__header");
    this.body = this.querySelector(".bundle-builder-step__body");
    this.header.addEventListener("click", this.onHeaderClick.bind(this));
  }

  onHeaderClick() {
    if (this.classList.contains("disabled")) {
      this.highlightToSelectStep();
    } else {
      const allSteps = document.querySelectorAll(".bundle-builder-step");

      // 1. Show/hide current step
      if (this.classList.contains("active")) {
        this.classList.remove("active");
      } else {
        allSteps.forEach((el) => el.classList.remove("active"));
        this.classList.add("active");
      }
    }
    BundleBuilder.checkATCButton();
  }

  highlightToSelectStep() {
    const toSelectStep = document.querySelector(
      ".bundle-builder-step.to-select"
    );
    if (!toSelectStep) return;

    toSelectStep.classList.add("highlight-to-select");
    setTimeout(
      () => toSelectStep.classList.remove("highlight-to-select"),
      1000
    );
  }
}

customElements.define("bundle-builder-step", BundelBuilderStep);
